import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import type { Product } from '@/types/product';
import { getPriceStr } from '@/types/product';

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cart: CartItem[];
  totalPrice: number;
  onUpdateQuantity: (productId: number, quantity: number) => void;
  onRemove: (productId: number) => void;
}

export function CartDrawer({ isOpen, onClose, cart, totalPrice, onUpdateQuantity, onRemove }: CartDrawerProps) {
  // Prüfen, ob ein Artikel "Preis auf Anfrage" hat
  const hasInquiryPrice = cart.some(item => item.product.price === null);
  
  // Prüfen, ob der Warenkorb ausschließlich kostenlose Artikel enthält
  const onlyFreeItems = cart.length > 0 && cart.every(item => item.product.price === 0);

  return (
    <>
      <div className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />

      <div className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-surface-primary border-l border-white/[0.06] shadow-2xl transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
            <ShoppingBag size={20} className="text-gold" />Warenkorb
          </h2>
          <button onClick={onClose} className="p-2 text-text-muted hover:text-text-primary transition-colors rounded-lg hover:bg-white/[0.05]"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag size={48} className="text-text-muted mb-4" />
              <p className="text-text-secondary font-medium mb-1">Dein Warenkorb ist leer</p>
              <p className="text-text-muted text-sm mb-6">Entdecke unsere Produkte im Shop</p>
              <Link to="/shop" onClick={onClose} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all">
                Zum Shop
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {cart.map(item => (
                <div key={item.product.id} className="flex gap-4 bg-surface rounded-xl p-3 border border-white/[0.06]">
                  <img src={item.product.image} alt={item.product.name} className="w-20 h-20 object-cover rounded-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-text-primary truncate">{item.product.name}</h3>
                    <p className="text-gold font-bold text-sm mt-0.5">{getPriceStr(item.product.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white/[0.05] text-text-secondary hover:bg-white/[0.1] hover:text-text-primary transition-colors"><Minus size={14} /></button>
                      <span className="text-sm font-medium text-text-primary w-6 text-center">{item.quantity}</span>
                      <button onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center rounded-md bg-white/[0.05] text-text-secondary hover:bg-white/[0.1] hover:text-text-primary transition-colors"><Plus size={14} /></button>
                      <button onClick={() => onRemove(item.product.id)} className="ml-auto p-1.5 text-text-muted hover:text-crimson transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="p-6 border-t border-white/[0.06] space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-text-secondary">Zwischensumme</span>
              <span className="text-lg font-bold text-gold">
                {hasInquiryPrice 
                  ? 'Auf Anfrage' 
                  : onlyFreeItems 
                    ? 'Kostenlos' 
                    : `${totalPrice.toFixed(2).replace('.', ',')} €`}
              </span>
            </div>
            <p className="text-text-muted text-xs">
              {hasInquiryPrice 
                ? 'Gesamtpreis wird individuell ermittelt.' 
                : 'Versandkosten werden im Checkout berechnet.'}
            </p>
            <Link to="/checkout" onClick={onClose}
              className="block w-full text-center py-3 bg-gold text-surface-primary font-bold rounded-xl hover:shadow-gold-lg transition-all">
              Zur Kasse →
            </Link>
            <button onClick={onClose} className="block w-full text-center py-2 text-text-muted text-sm hover:text-text-primary transition-colors">
              Weiter einkaufen
            </button>
          </div>
        )}
      </div>
    </>
  );
}